import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllExportFiles } from 'src/shared/services/exportFileService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box, Button } from '@mui/material';
import { Download } from '@mui/icons-material';
import { downloadExportFile } from 'src/shared/functions/downloadExportFile';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import dayjs from 'dayjs';
import { ExportFileResponse } from '../models/exportFileResponse';
import { ExportFileSearchFilter } from '../models/exportFilterSearchFilter';
import { MyExportsFilePresentation } from './myExportsFilePresentation';

type MyExportsDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: ExportFileSearchFilter;
  searchFilterCallback: (filter: ExportFileSearchFilter) => void;
};

interface DisplayExportFileResponse extends ExportFileResponse {
  download: string;
}

export const MyExportsDataGrid = (props: MyExportsDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['file']);
  const isMounted = useIsMounted();

  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);

  const [rows, setRows] = useState<DisplayExportFileResponse[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 15, 25, 50],
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllExportFiles(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(
            result.data.results.map((item) => {
              return { ...item, download: '' };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted]);

  const downloadCallback = useCallback((isLoading: boolean) => {
    setDownloadingFile(isLoading);
  }, []);

  const downloadFile = async (id, fileName, fileType) => {
    downloadExportFile(id, fileName, fileType, downloadCallback);
  };

  const columns: GridColDef[] = [
    {
      field: 'filename',
      headerName: t('file:tableHeaders.exportType'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <MyExportsFilePresentation exportFileResponse={params.row} />;
      },
    },
    {
      field: 'createdAt',
      headerName: t('file:tableHeaders.createdAt'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdAt) || ''}</>;
      },
    },
    {
      field: 'createdBy',
      headerName: t('file:tableHeaders.createdBy'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.createdBy}</>;
      },
    },
    {
      field: 'mediaType',
      headerName: t('file:tableHeaders.mediaType'),
      sortable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.mediaType}</>;
      },
    },

    {
      field: 'id',
      headerName: '',
      sortable: false,
      flex: 0.1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.id}</>;
      },
    },
    {
      field: 'download',
      headerName: ' ',
      sortable: false,
      minWidth: 130,
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return (
          <Button
            endIcon={<Download fontSize='small' />}
            sx={{ whiteSpace: 'nowrap' }}
            onClick={(e) => {
              e.stopPropagation();
              const exportType = t(`common:exportFileType.${params.row.exportType}`);
              const dateTime = `${DateHelper.getFSDate(params.row.createdAt)}_${dayjs(params.row.createdAt).format('HHMM')}`;
              const fileName = `${exportType}_${dateTime}.${params.row.fileExtension}`;
              downloadFile(params.row.id, fileName, params.row.filetype);
            }}
          >
            {t('file:downloadButton')}
          </Button>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
    >
      <LoadingOverlay message={t('file:downloadInProgress')} loading={downloadingFile} />
      <DataGrid<DisplayExportFileResponse>
        columns={columns}
        rows={rows}
        autoHeight
        columnVisibilityModel={{ id: false }}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        disablePointer
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          ConsoleHelper.log('newSortModel', newSortModel);
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
